import { type StrictEntries, type UtcMilliseconds } from '@pkg/types';

import { flipNumberSign } from './numbers';

// TODO: We should convert this file to a `TimeBreakdown` class.

export interface TimeData {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
    past: boolean;
}

export interface Time24hData {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    // Consider including properties: `milliseconds`, `past`.
}

type TimeDataMeasurements = keyof Omit<TimeData, 'past'>;

export type TimeDataEntries = StrictEntries<TimeData>;
export type TimeDataSuffix = Record<TimeDataMeasurements, string>;

const monthsPerYear = 12;

const weeksPerMonth = 4;
const weeksPerYear = 52;

const daysPerWeek = 7;
const daysPerMonth = 30;
const daysPerYear = 365;

const hrsPerDay = 24;
const hrsPerWeek = hrsPerDay * daysPerWeek;
const hrsPerMonth = hrsPerDay * daysPerMonth;
const hrsPerYear = hrsPerDay * daysPerYear;

const minsPerHr = 60;
const minsPerDay = minsPerHr * hrsPerDay;
const minsPerWeek = minsPerDay * daysPerWeek;
const minsPerMonth = minsPerDay * daysPerMonth;
const minsPerYear = minsPerDay * daysPerYear;

const secsPerMin = 60;
const secsPerHr = secsPerMin * minsPerHr;
const secsPerDay = secsPerHr * hrsPerDay;
const secsPerWeek = secsPerDay * daysPerWeek;
const secsPerMonth = secsPerDay * daysPerMonth;
const secsPerYear = secsPerDay * daysPerYear;

const msPerSec = 1000;
const msPerMin = msPerSec * secsPerMin;
const msPerHr = msPerMin * minsPerHr;
const msPerDay = msPerHr * hrsPerDay;
const msPerWeek = msPerDay * daysPerWeek;
const msPerMonth = msPerDay * daysPerMonth;
const msPerYear = msPerDay * daysPerYear;

export const timeMeasurement = {
    monthsPerYear,
    weeksPerMonth,
    weeksPerYear,
    daysPerWeek,
    hrsPerDay,
    hrsPerWeek,
    hrsPerMonth,
    hrsPerYear,
    minsPerHr,
    minsPerDay,
    minsPerWeek,
    minsPerMonth,
    minsPerYear,
    secsPerMin,
    secsPerHr,
    secsPerDay,
    secsPerWeek,
    secsPerMonth,
    secsPerYear,
    msPerSec,
    msPerMin,
    msPerHr,
    msPerDay,
    msPerWeek,
    msPerMonth,
    msPerYear,
};

export const timeSuffixShorthand: TimeDataSuffix = {
    years: 'y',
    months: 'mm',
    weeks: 'w',
    days: 'd',
    hours: 'h',
    minutes: 'm',
    seconds: 's',
    milliseconds: 'ms',
};

export const timeTenseSuffix = {
    past: 'ago',
    future: 'until',
};

export function flip24hTimeSign({ days, hours, minutes, seconds }: Time24hData) {
    return {
        days: flipNumberSign(days),
        hours: flipNumberSign(hours),
        minutes: flipNumberSign(minutes),
        seconds: flipNumberSign(seconds),
    };
}

export function msTo24h(ms: UtcMilliseconds): Time24hData {
    // TODO: Instead of returning negative numbers, we should do
    // what `msToTime()` does and return a `past?: boolean;` value.
    const isNegative = Math.sign(ms) === -1;
    const forcedPositiveMs = Math.abs(ms);

    const totalSeconds = Math.floor(forcedPositiveMs / msPerSec);
    const totalMinutes = Math.floor(totalSeconds / secsPerMin);
    const totalHours = Math.floor(totalMinutes / minsPerHr);

    const time = {
        days: Math.floor(totalHours / hrsPerDay),
        hours: totalHours % hrsPerDay,
        minutes: totalMinutes % minsPerHr,
        seconds: totalSeconds % secsPerMin,
    };

    return isNegative ? flip24hTimeSign(time) : time;
}

export function msToTime(ms: UtcMilliseconds): TimeData {
    const forcedPositiveMs = Math.abs(ms);

    const totalSeconds = Math.floor(forcedPositiveMs / msPerSec);
    const totalMinutes = Math.floor(totalSeconds / secsPerMin);
    const totalHours = Math.floor(totalMinutes / minsPerHr);
    const totalDays = Math.floor(totalHours / hrsPerDay);
    const totalWeeks = Math.floor(totalDays / daysPerWeek);
    const totalMonths = Math.floor(totalDays / daysPerMonth);

    return {
        years: Math.floor(totalDays / daysPerYear),
        months: totalMonths % monthsPerYear,
        weeks: totalWeeks % weeksPerMonth,
        days: totalDays % daysPerWeek,
        hours: totalHours % hrsPerDay,
        minutes: totalMinutes % minsPerHr,
        seconds: totalSeconds % secsPerMin,
        milliseconds: forcedPositiveMs % (totalSeconds * msPerSec),
        past: Math.sign(ms) === -1,
    };
}

export function timeUtcMsDiff(targetUtcMs: UtcMilliseconds) {
    return targetUtcMs - Date.now();
}

export function timeLabelParts(data: TimeData) {
    const entries = Object.entries(data) as TimeDataEntries;

    // Consider supporting `shorthand` vs `full` suffixes.
    const parts = entries.reduce<string[]>((collection, [key, value]) => {
        if (value === 0) return collection;

        return key === 'past'
            ? [...collection, value ? timeTenseSuffix.past : timeTenseSuffix.future]
            : [...collection, `${value}${timeSuffixShorthand[key]}`];
    }, []);

    return parts;
}
